import { useEffect, useState } from 'react'
import { api } from '../Services/api'
import { LANG } from '../i18n'

/* Small shared lists (categories, sources, trending topics), fetched once and
   reused by every screen for `ttl` ms. */
const cache = new Map()

function load(key, ttl, fetcher) {
  const hit = cache.get(key)
  if (hit && (hit.pending || Date.now() - hit.at < ttl)) return hit.promise
  const entry = { at: Date.now(), pending: true, data: hit?.data }
  entry.promise = fetcher()
    .then((data) => {
      Object.assign(entry, { data, pending: false, at: Date.now() })
      return data
    })
    .catch((error) => {
      // A failed refresh keeps the old data; a first failure is retried next time.
      if (hit?.data !== undefined) {
        Object.assign(entry, { pending: false, data: hit.data, at: Date.now() })
        return hit.data
      }
      cache.delete(key)
      throw error
    })
  cache.set(key, entry)
  return entry.promise
}

export function useResource(
  key,
  fetcher,
  { ttl = 60000, initial = undefined, enabled = true } = {}
) {
  const [state, setState] = useState(() => ({
    key,
    data: cache.get(key)?.data ?? initial,
    error: null,
  }))
  useEffect(() => {
    if (!enabled) return undefined
    let alive = true
    setState({ key, data: cache.get(key)?.data ?? initial, error: null })
    load(key, ttl, fetcher)
      .then((data) => alive && setState({ key, data, error: null }))
      .catch(
        (error) =>
          alive && setState((s) => ({ key, data: s.key === key ? s.data : initial, error }))
      )
    return () => {
      alive = false
    }
    // fetcher and initial are defined inline by callers; the key identifies them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled])
  // Only this key's data: never the previous topic's while the next one loads.
  return state.key === key ? state : { key, data: cache.get(key)?.data ?? initial, error: null }
}

// Spec 9.8 order; shown until /categories answers (names come from the API).
const FALLBACK_CATEGORIES = [
  ['politika', 'Politika'],
  ['crna-hronika', LANG === 'hr' ? 'Crna kronika' : 'Crna hronika'],
  ['sport', 'Sport'],
  ['ekonomija', 'Ekonomija'],
  ['svijet', LANG === 'sr-Latn' ? 'Svet' : 'Svijet'],
  ['magazin', 'Magazin'],
  ['zdravlje', 'Zdravlje'],
  ['tech', 'Tech'],
].map(([slug, title]) => ({ slug, title }))

export function useCategories() {
  const { data } = useResource('categories', () => api.categories().then((d) => d.categories), {
    ttl: 10 * 60000,
    initial: FALLBACK_CATEGORIES,
  })
  return data || FALLBACK_CATEGORIES
}

export function useSources() {
  const { data, error } = useResource('sources', () => api.sources().then((d) => d.sources), {
    ttl: 10 * 60000,
  })
  return { sources: data || [], loaded: data !== undefined, error }
}

export function useTrending({ category = null, limit = 15, enabled = true } = {}) {
  const key = `trending:${category || ''}:${limit}`
  const { data, error } = useResource(
    key,
    () => api.trending({ category, limit }).then((d) => d.topics),
    { ttl: 5 * 60000 /* the list changes every 10 min (spec 9.7a) */, enabled }
  )
  return { topics: data || [], loaded: data !== undefined, error }
}
